import * as amplitude from '@amplitude/analytics-browser';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import { AnyRouter } from '@tanstack/react-router';

import {
  environment,
  muiLicenseKey,
  sentryDsn,
  version,
} from '~/constants/environment';

import { isLocalhost, isProductionEnvironment } from '~/utils/environment';
import GlobalStorage from '~/utils/GlobalStorage';

import AuthService from './auth.service';
import LocalStorageService from './localStorage.service';

// Create Custom Sentry Route component
class ThirdPartyService {
  initMuiPro() {
    LicenseInfo.setLicenseKey(muiLicenseKey);
  }

  initSentry(router: AnyRouter) {
    if (LocalStorageService.sentryGoogleAnalyticsDisabled()) {
      return;
    }

    if (Sentry.isInitialized()) {
      // Sentry is already initialized
      return;
    }

    Sentry.init({
      beforeBreadcrumb(breadcrumb) {
        // Keep console breadcrumbs — they are valuable context in bug reports.
        // Drop noisy UI click breadcrumbs (high volume, low signal).
        return breadcrumb.category?.includes('ui.click') ? null : breadcrumb;
      },
      denyUrls: [/preview-customer/], // Don't track requests to preview deployments
      dsn: sentryDsn,
      enabled: !isLocalhost,
      environment,
      ignoreErrors: [
        'No matching state found in storage',
        'Request failed with status code 401',
        'Service worker update skipped due to authentication redirect',
        // An expired session is a normal logout, not a fault. A refresh that
        // fails for any other reason throws a generic error and still reports.
        // Kept in sync with the same entry in `packages/sentry/src/sentry.ts`,
        // which the apps that init from that package use instead of this one.
        'SessionExpiredError',
        /Object Not Found Matching Id:\d+, MethodName:.+/,
      ],
      integrations: [
        Sentry.browserProfilingIntegration(),
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        // Vestigas support/internal users see our custom `BugReportButton` /
        // `BugReportDialog` (see `~/modules/bugReport`), which enriches every
        // report with the current user's feature flags, permissions, route,
        // and viewport. Everyone else gets this form via `SentryFeedbackWidget`.
        //
        // `autoInject: false` suppresses Sentry's own trigger; the form is
        // created on demand instead.
        Sentry.feedbackIntegration({
          addScreenshotButtonLabel: 'Screenshot hinzufügen',
          autoInject: false,
          cancelButtonLabel: 'Abbrechen',
          colorScheme: 'system',
          confirmButtonLabel: 'Bestätigen',
          emailLabel: 'E-Mail-Adresse',
          emailPlaceholder: 'name@domain.com',
          formTitle: 'Fehler melden',
          isEmailRequired: false,
          isNameRequired: false,
          isRequiredLabel: '(Pflichtfeld)',
          messageLabel: 'Beschreibung',
          messagePlaceholder:
            'Was ist der Fehler? Was hätte eigentlich passieren sollen?',
          nameLabel: 'Name',
          namePlaceholder: 'Dein Name',
          removeScreenshotButtonLabel: 'Screenshot löschen',
          showBranding: false,
          submitButtonLabel: 'Senden',
          successMessageText: 'Danke für deine Fehlermeldung!',
        }),
        Sentry.replayIntegration(),
        Sentry.eventFiltersIntegration(),
      ],

      // Increase normalization depth to show full object details
      maxValueLength: 1000,
      normalizeDepth: 10,

      profilesSampleRate: 1,
      release: `web-customer-portal@${version}`,

      // Reduce this as we gain users in production!
      // Session Replay
      replaysOnErrorSampleRate: 1,
      replaysSessionSampleRate: 0.1,

      // Reduce this as we gain users in production!
      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: [/^\//, /^https:\/\/app\.vestigas\.com\/api/],

      // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing.
      tracesSampleRate: 1,
    });
  }

  initSentryUser(companyName: string, companyId: string) {
    const userId = AuthService.getUserIdFromAccessToken();

    Sentry.setUser({ id: userId });
    Sentry.setTags({
      ['company.name']: companyName,
      ['company.id']: companyId,
    });
  }

  initProductAnalyticsTools(featureFlag, companyName) {
    if (!featureFlag) {
      return;
    }

    if (!isProductionEnvironment) {
      return;
    }

    const userId = AuthService.getUserIdFromAccessToken();

    if (!userId) {
      return;
    }

    // Init and disable IP tracking
    amplitude.init('9b5a3c6fe13909baba504ba823f59263', {
      serverZone: 'EU',
      trackingOptions: {
        ipAddress: false,
      },
    });

    amplitude.setUserId(userId);

    const identifyEvent = new amplitude.Identify();
    identifyEvent.setOnce('company', companyName);
    amplitude.identify(identifyEvent);

    // Tracking Amplitude events before the libraries have been initialized leads to bugs.
    // Thus, use this variable as a Boolean to track whether the libraries have already been initialized.
    GlobalStorage.productAnalyticsToolsInitialized = true;
  }

  /*
   * @see https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/#opt-users-out-of-tracking
   */
  optOutProductAnalyticsTools() {
    if (!GlobalStorage.productAnalyticsToolsInitialized) {
      return;
    }

    amplitude.setOptOut(true);

    const userId = AuthService.getUserIdFromAccessToken();
    LocalStorageService.setLocalStorage(
      `product_analytics_tools_opted_out_${userId}`,
      '1',
    );
  }

  optInProductAnalyticsTools() {
    if (!GlobalStorage.productAnalyticsToolsInitialized) {
      return;
    }

    amplitude.setOptOut(false);

    const userId = AuthService.getUserIdFromAccessToken();
    LocalStorageService.setLocalStorage(
      `product_analytics_tools_opted_out_${userId}`,
      '0',
    );
  }

  productAnalyticsToolsOptedOut() {
    const userId = AuthService.getUserIdFromAccessToken();

    const hasOptedOut =
      LocalStorageService.getLocalStorage(
        `product_analytics_tools_opted_out_${userId}`,
      ) === '1';

    return hasOptedOut;
  }
}

const thirdPartyServiceInstance = new ThirdPartyService();

export default thirdPartyServiceInstance;
